import styled from "@emotion/styled";
import { get, set, union, uniqBy } from "lodash";
import React from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import { APP_CONFIG } from "config/appConfig";
import { SensorTypeObject, DeviceSelected, Device } from "types";
import { displaySensorType } from "utils/functions";
import { getSensorTypes, isRoomGroupZoneDevice } from "utils/gatewayFunctions";
import DashboardCreateModal, { DashboardCreateModalProps } from ".";
import { useCreateDashboardStore } from "..";
import { NetworkOBJ } from "constant/gateway";

const StyledDashboardCreateModal = styled(DashboardCreateModal)`
    .modal-dialog {
        width: 650px;
        max-width: 650px;
    }
    .modal-option .custom-control-label:before,
    .modal-option .custom-control-label:after {
        top: 50%;
        transform: translateY(-50%);
    }
    .modal-options {
        position: relative;
        min-height: 10vh;
        max-height: 50vh;
        overflow-y: auto;
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
    }
    .modal-options::-webkit-scrollbar {
        display: none;
    }

    @media (max-width: 768px) {
        .modal-dialog {
            width: unset !important;
            max-width: unset !important;
        }
    }
`;

const DashboardCreateSelectDeviceModal: React.FunctionComponent<
    DashboardCreateModalProps & {
        deviceType: "Sensor" | "Actuator";
        sensorTypes?: string[];
        filter?: (d: DeviceSelected) => boolean;
        maxDevices?: number;
        multipleSelection?: boolean;
        excludeDevices?: string[];
        allowSensorFilter?: boolean;
    }
> = (props) => {
    const [filteredDevices, setFilteredDevices] = React.useState<
        DeviceSelected[]
    >([]);
    const [searchCondition, setSearchCondition] = React.useState<{
        name: string;
        gatewayId: string;
        sensorType: string;
    }>({
        name: "",
        gatewayId: "",
        sensorType: "",
    });

    const {
        show,
        onHide,
        onSave,
        maxDevices,
        deviceType,
        sensorTypes,
        multipleSelection = true,
        excludeDevices = [],
        allowSensorFilter = true,
        filter,
    } = props;
    const [values, setValues] = React.useState<any>({});
    const [sensorTypeList, setSensorTypeList] = React.useState<
        SensorTypeObject[]
    >([]);
    const isOnPrem = APP_CONFIG.APP_IS_ONPREM;

    const { deviceList, allRegistry, loading } = useCreateDashboardStore();

    // This Map used to keep the registry name and whether it is a network or gateway
    const registryLookupByGatewayId = React.useMemo(
        () =>
            new Map(
                (allRegistry || []).map((registry: any) => [
                    registry.gateway_id,
                    {
                        name: registry.name,
                        isNetwork: get(registry, "info.OBJ") === NetworkOBJ,
                    },
                ])
            ),
        [allRegistry]
    );
    const isSelectAll =
        !!filteredDevices.length &&
        !!Object.keys(values).length &&
        Object.keys(values).length ===
            union(
                Object.keys(values),
                Object.keys(filteredDevices.pluck("deviceId"))
            ).length;

    const selectedCount = Object.keys(values).length;
    const selectedCountText =
        String(selectedCount) + (maxDevices ? `/${maxDevices}` : "");

    const getRegistryOptionLabel = (name: string, gatewayId: string) => {
        if (!isOnPrem) {
            return name;
        }

        return `${name} (${
            registryLookupByGatewayId.get(gatewayId)?.isNetwork
                ? "Network"
                : "Gateway"
        })`;
    };

    const filterFunction = (device: DeviceSelected) =>
        (!searchCondition.name ||
            device.sensorName
                .toLowerCase()
                .includes(searchCondition.name.toLowerCase().trim())) &&
        (!searchCondition.gatewayId ||
            device.gatewayId === searchCondition.gatewayId) &&
        (!searchCondition.sensorType ||
            device.sensorType === searchCondition.sensorType ||
            get(device.app, `[${device.said}].NAME`) ===
                searchCondition.sensorType) &&
        (!sensorTypes ||
            sensorTypes.includes(device.sensorType) ||
            sensorTypes.includes(get(device.app, `[${device.said}].NAME`)));

    const getDeviceList = () => {
        const filterActuator = !deviceType || deviceType === "Actuator";

        // Device like Room/Group/Zone from PSL no need to show
        const filterRoomGroupZone = (device: Device) => {
            return isRoomGroupZoneDevice(device);
        };

        let filteredDeviceList = uniqBy(
            deviceList,
            (d) => d.deviceId + d.gatewayId
        )
            .filter((device) => device.isActuator === filterActuator)
            .filter((device) => !excludeDevices.includes(device.deviceId))
            .filter(filterFunction)
            .filter((device) => !filterRoomGroupZone(device))
            .filter((device) => (filter ? filter(device) : true))
            .sort(
                (a, b) => a.sensorName?.localeCompare(b.sensorName || "") || 0
            );
        return filteredDeviceList;
    };

    React.useEffect(() => {
        if (show && deviceList.length > 0) {
            // When value is a string list
            if (Array.isArray(props.values)) {
                const selectedGateways = {} as any;
                props.values.forEach((value: any) => {
                    set(
                        selectedGateways,
                        value,
                        deviceList.find((item) => item.name === value)
                    );
                });
                setValues(selectedGateways);
            } else {
                setValues(props.values || {});
            }

            const filterDeviceList = getDeviceList();

            setFilteredDevices(filterDeviceList);

            let sensorTypeList = getSensorTypes(filterDeviceList).filter(
                ({ id }) => !sensorTypes || sensorTypes.includes(id)
            );

            sensorTypeList = sensorTypeList.sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            );

            setSensorTypeList(sensorTypeList);
        }

        setSearchCondition({
            name: "",
            gatewayId: "",
            sensorType: "",
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, deviceList]);

    React.useEffect(() => {
        setFilteredDevices(getDeviceList());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCondition, sensorTypes]);

    const handleSelectDevice = (
        e: React.ChangeEvent<HTMLInputElement>,
        device: DeviceSelected
    ) => {
        const checked = e.target.checked;
        const newValues = { ...values };

        if (multipleSelection) {
            if (checked) {
                newValues[device.deviceId] = device;
            } else {
                delete newValues[device.deviceId];
            }

            setValues(newValues);
            return;
        }

        setValues({
            [device.deviceId]: device,
        });
    };

    const handleToggleAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        const newValues = { ...values };

        if (checked) {
            // Check max devices limit before adding
            if (
                maxDevices &&
                Object.keys(values).length + filteredDevices.length > maxDevices
            ) {
                return;
            }

            filteredDevices.forEach((device) => {
                newValues[device.deviceId] = device;
            });
        } else {
            filteredDevices.forEach((device) => {
                delete newValues[device.deviceId];
            });
        }

        setValues(newValues);
    };

    const renderLoading = () => {
        return filteredDevices.length > 0 ? (
            <Spinner animation="border" variant="primary" />
        ) : (
            <p className="w-100 d-flex justify-content-center">
                No {props.deviceType.toLowerCase()}s to display
            </p>
        );
    };

    const renderFooter = () => {
        return (
            <div className="row w-100 justify-content-between align-items-center">
                <strong className="text-gray">
                    {!!selectedCount &&
                        `${selectedCountText} ${props.deviceType}(s) are selected`}
                </strong>
                <Button
                    variant="primary"
                    onClick={(event) => {
                        onHide?.();
                        onSave?.(event, values);
                    }}
                >
                    ADD
                </Button>
            </div>
        );
    };

    return (
        <StyledDashboardCreateModal
            title={"Select {deviceType}s".fill(props)}
            show={show}
            onHide={() => onHide?.()}
            footer={renderFooter}
        >
            {allRegistry?.length > 0 && (
                <Row className="mb-2 p-3 border rounded bg-light-gray">
                    <Col sm="7" className="m-0 p-0">
                        <InputGroup className="text-gray flex align-items-center ">
                            <span className="mr-3">Filter By:</span>
                            <Form.Control
                                className="rounded"
                                as="select"
                                type="text"
                                placeholder="Search.."
                                size="sm"
                                onChange={(e) =>
                                    setSearchCondition({
                                        ...searchCondition,
                                        gatewayId: e.target.value,
                                    })
                                }
                                custom
                            >
                                <option key="all" value="">
                                    {isOnPrem
                                        ? "All Gateways/Networks"
                                        : "All Gateways"}
                                </option>
                                {allRegistry
                                    .filter((registry) => registry.info)
                                    .map(({ name, gateway_id }: any) => (
                                        <option
                                            key={gateway_id}
                                            value={gateway_id}
                                        >
                                            {getRegistryOptionLabel(
                                                name,
                                                gateway_id
                                            )}
                                        </option>
                                    ))}
                            </Form.Control>
                        </InputGroup>
                    </Col>
                    <Col sm="5" className="m-0 p-0">
                        {deviceType === "Sensor" && allowSensorFilter && (
                            <Form.Control
                                className="rounded ml-3"
                                as="select"
                                type="text"
                                placeholder="Search.."
                                size="sm"
                                onChange={(e) =>
                                    setSearchCondition({
                                        ...searchCondition,
                                        sensorType: e.target.value,
                                    })
                                }
                                custom
                            >
                                <option value="">All Types</option>
                                {sensorTypeList.map(({ id, name }) => (
                                    <option key={id} value={id}>
                                        {name}
                                    </option>
                                ))}
                            </Form.Control>
                        )}
                    </Col>
                </Row>
            )}
            <Row className="align-items-center mb-3 modal-filter">
                {multipleSelection && (
                    <Col
                        sm="4"
                        className="modal-option text-gray-important mx-0 p-0"
                    >
                        <Form.Check
                            id="device-check-all"
                            type="checkbox"
                            label="Select All"
                            disabled={
                                maxDevices !== undefined &&
                                filteredDevices.length > Number(maxDevices || 0)
                            }
                            custom
                            checked={isSelectAll}
                            onChange={(event) => handleToggleAll(event)}
                        />
                    </Col>
                )}
                <Col
                    sm={multipleSelection ? "8" : "12"}
                    className={multipleSelection ? "pl-lg-3 pr-0" : "p-0"}
                >
                    <Form.Control
                        type="text"
                        placeholder="Search.."
                        onChange={(e) =>
                            setSearchCondition({
                                ...searchCondition,
                                name: e.target.value,
                            })
                        }
                    ></Form.Control>
                </Col>
            </Row>
            <Form className="d-flex flex-wrap modal-options">
                {!!filteredDevices.length && !loading
                    ? filteredDevices.map((subDevice: any, index: number) => {
                          const isNetwork =
                              registryLookupByGatewayId.get(subDevice.gatewayId)
                                  ?.isNetwork || false;
                          return (
                              <div
                                  className="modal-option"
                                  key={subDevice.deviceId + subDevice.gatewayId}
                              >
                                  <Form.Check
                                      name="selected-device"
                                      value={subDevice.deviceId}
                                      checked={values?.hasOwnProperty(
                                          subDevice.deviceId
                                      )}
                                      disabled={
                                          maxDevices !== undefined &&
                                          !get(values, subDevice.deviceId) &&
                                          Object.keys(values).length >=
                                              maxDevices
                                      }
                                      onChange={(e) =>
                                          handleSelectDevice(e, subDevice)
                                      }
                                      id={index + subDevice.deviceId}
                                      custom
                                      type={
                                          multipleSelection
                                              ? "checkbox"
                                              : "radio"
                                      }
                                      label={
                                          <div>
                                              <h4
                                                  style={{
                                                      whiteSpace: "pre-wrap",
                                                  }}
                                              >
                                                  {subDevice.sensorName}
                                              </h4>{" "}
                                              <p>
                                                  {isNetwork
                                                      ? registryLookupByGatewayId.get(
                                                            subDevice.gatewayId
                                                        )?.name
                                                      : subDevice.busName}
                                              </p>
                                              <p>
                                                  {isNetwork
                                                      ? "Auxiliary Hub UUID"
                                                      : "LDSU UUID"}
                                                  : {subDevice.ldsuUID}
                                              </p>
                                              <p>
                                                  {displaySensorType(subDevice)}
                                              </p>
                                          </div>
                                      }
                                      className="float-left"
                                  />
                              </div>
                          );
                      })
                    : renderLoading()}
            </Form>
        </StyledDashboardCreateModal>
    );
};

export default React.memo(DashboardCreateSelectDeviceModal);
